import Vue from "vue";
import lodashGet from "lodash/get";
import queryString from "query-string";
import { v4 as uuidv4 } from "uuid";

const cuilGen = function (numeroDocumento, sexo) {
  /**
   * Cuil format is: AB - document_number - C
   * Author: Nahuel Sanchez, Woile
   *
   * @param {str} document_number -> string solo digitos
   * @param {str} gender -> debe contener HOMBRE, MUJER o SOCIEDAD
   *
   * @return {str}
   **/
  let HOMBRE = ["HOMBRE", "M", "MALE"];
  let MUJER = ["MUJER", "F", "FEMALE"];
  let SOCIEDAD = ["SOCIEDAD", "S", "SOCIETY"];
  let AB, C;
  let largoDNI = numeroDocumento.length;

  /**
   * Verifico que el document_number tenga exactamente ocho numeros y que
   * la cadena no contenga letras.
   */
  if (isNaN(numeroDocumento) || largoDNI >= 9 || largoDNI < 7) {
    Vue.$log.error("El numero de documento ingresado no es correcto.");
    return "";
  }

  if (largoDNI === 7) {
    numeroDocumento = "0".concat(numeroDocumento);
  }

  /**
   * De esta manera permitimos que el gender venga en minusculas,
   * mayusculas y titulo.
   */
  sexo = sexo.toUpperCase();

  // Defino el valor del prefijo.
  if (HOMBRE.indexOf(sexo) >= 0) {
    AB = "20";
  } else if (MUJER.indexOf(sexo) >= 0) {
    AB = "27";
  } else {
    AB = "30";
  }

  /*
   * Los numeros (excepto los dos primeros) que le tengo que
   * multiplicar a la cadena formada por el prefijo y por el
   * numero de document_number los tengo almacenados en un arreglo.
   */
  let multiplicadores = [3, 2, 7, 6, 5, 4, 3, 2];

  // Realizo las dos primeras multiplicaciones por separado.
  let calculo = parseInt(AB.charAt(0)) * 5 + parseInt(AB.charAt(1)) * 4;

  /*
   * Recorro el arreglo y el numero de document_number para
   * realizar las multiplicaciones.
   */
  for (let i = 0; i < 8; i++) {
    calculo += parseInt(numeroDocumento.charAt(i)) * multiplicadores[i];
  }

  // Calculo el resto.
  let resto = parseInt(calculo) % 11;

  /*
   * Llevo a cabo la evaluacion de las tres condiciones para
   * determinar el valor de C y conocer el valor definitivo de
   * AB.
   */
  if (SOCIEDAD.indexOf(sexo) < 0 && resto === 1) {
    if (HOMBRE.indexOf(sexo) >= 0) {
      C = "9";
    } else {
      C = "4";
    }
    AB = "23";
  } else if (resto === 0) {
    C = "0";
  } else {
    C = 11 - resto;
  }

  // Generate cuit
  return [AB, numeroDocumento, C].join("");
};

const replaceAll = function (str, search, replacement) {
  return str.split(search).join(replacement);
};

const getParameterByName = (name, url) => {
  if (!url) url = window.location.href;
  let results = new RegExp(
    "[?&]" + name.replace(/[[\]]/g, "\\$&") + "(=([^&#]*)|&|#|$)"
  ).exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};

const getTrackingCodeLocal = name => {
  let tc = getParameterByName(name, window.location.href);

  if (tc != null && tc !== "") {
    return tc;
  }
  return null;
};

const formatTasa = num => {
  if (typeof num == "string") {
    num = Number(num.replace(",", "."));
  }
  return parseFloat(Number(num)).toFixed(2);
};

const formatDinero = (num, decimal = false) => {
  const regex = /^\d{1,3}(\.\d{3})*,\d{2}$/;
  if (regex.test(num)) return num;

  if (typeof num == "string") {
    num = Number(num.replace(",", "."));
  }

  if (decimal) {
    return new Intl.NumberFormat("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  }
  return new Intl.NumberFormat("es-AR").format(num);
};

const obtenerTrackingCode = function () {
  let keys = ["cid", "onsite", "ema", "psm", "dis", "afi", "afl"];
  let tc = "";

  for (let key of keys) {
    let tccan = getTrackingCodeLocal(key);
    if (tccan !== null) {
      tc = tccan;
      break;
    }
  }

  if (tc === "") {
    return "";
  }

  let tcSplit = "";
  let tcBuild = "";

  if (tc.indexOf(":") === -1) {
    // Tracking codes viejos
    tcSplit = tc.split("-");
  } else {
    // Tracking codes nuevos
    replaceAll(tc, "_", " ");
    tcSplit = tc.split("-")[0].split(":");
  }

  tcBuild =
    tcSplit[0].trim() +
    "-" +
    tcSplit[1].trim() +
    "-" +
    tcSplit[2].trim() +
    "-" +
    tcSplit[3].trim();

  return "-" + tcBuild;
};

const toTitleCase = function (str) {
  if (!!str && str.trim() !== "") {
    return str.replace(
      /\w\S*/g,
      txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }
  return str;
};

const isDev = function () {
  return !PRODUCTION;
};

const addSeparatorsNF = (nStr, inD, outD, sep, precision = 0) => {
  nStr += "";
  let dpos = nStr.indexOf(inD);
  let nStrEnd = "";
  if (precision) {
    nStr = nStr.slice(0, -precision) + "," + nStr.slice(-precision);
  }
  if (dpos != -1) {
    nStrEnd = outD + nStr.substring(dpos + 1, nStr.length);
    nStr = nStr.substring(0, dpos);
  }
  let rgx = /(\d+)(\d{3})/;
  while (rgx.test(nStr)) {
    nStr = nStr.replace(rgx, "$1" + sep + "$2");
  }

  return nStr + nStrEnd;
};
const isValidDate = function (dateString, minAge, maxAge) {
  // Verificar patrón
  let regex_date = /\d{2}\/\d{2}\/\d{4}/;

  if (!regex_date.test(dateString)) {
    return false;
  }

  // Parsear string de fecha
  let parts = dateString.split("/");
  let day = parseInt(parts[0], 10);
  let month = parseInt(parts[1], 10);
  let year = parseInt(parts[2], 10);

  // Rango de meses y años
  if (year < 1000 || year > 3000 || month == 0 || month > 12) {
    return false;
  }

  // Ver edades permitidas
  if (minAge || maxAge) {
    // valor en UTC
    const clientBirth = new Date(`${year}-${month}-${day}`);
    const month_diff = Date.now() - clientBirth.getTime();
    const age_dt = new Date(month_diff);
    const clientAge = Math.abs(age_dt.getUTCFullYear() - 1970);
    if (minAge && clientAge < minAge) {
      return false;
    }
    if (maxAge && clientAge >= maxAge) {
      return false;
    }
  }

  let monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Ajustar años biciestos
  if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0)) {
    monthLength[1] = 29;
  }

  // Verificar rango de días
  return day > 0 && day <= monthLength[month - 1];
};

//formatea con separador de mil
const formatMoney = amount => {
  let amountInt = parseInt(amount);
  if (!amountInt) {
    return amount;
  }
  let valor = amountInt.toFixed(0);
  valor = addSeparatorsNF(valor, ".", ",", ".");
  return valor;
};

/* eslint-disable no-useless-escape */
/**
 * Verificar por el userAgent si el dispositivo es
 * mobile o no
 */
const _isMobile = () => {
  // Check for touch support
  const hasTouchSupport =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;

  // Check screen size
  const isSmallScreen = window.matchMedia("(max-width: 768px)").matches;

  return hasTouchSupport && isSmallScreen;
};

/**
 * Función de orden superior de lodash.get
 * que convierte null a undefined
 */
const get = (o, path, defval) => {
  return lodashGet(o, path, defval) === null
    ? defval
    : lodashGet(o, path, defval);
};

/**
 * Normalizar caracteres reemplazando acentuados por
 * sus equivalentes no acentuados
 */
const normalizeCharacters = str => {
  /** 2021-07-06: Merlin no normaliza las ENIES vienen como ? */
  return str
    ? (str + "")
        .replace(/[?=]/g, "N")
        .replace(/[#=]/g, "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
    : str;
};
/**
 *Generar un id Device
 **/
const idDevice = () => {
  let deviceID = MediaDeviceInfo.deviceId;
  if (typeof deviceID !== "undefined") {
    return deviceID;
  } else {
    let navigator_info = window.navigator;
    let screen_info = window.screen;
    let uid = navigator_info.mimeTypes.length;
    uid += navigator_info.userAgent.replace(/\D+/g, "");
    uid += navigator_info.plugins.length;
    uid += screen_info.height || "";
    uid += screen_info.width || "";
    uid += screen_info.pixelDepth || "";
    return uid;
  }
};

const isValidCuil = function (numeroCuil) {
  if (!numeroCuil) {
    return false;
  }

  let cuil = numeroCuil + "";

  if (/^[a-zA-Z]+$/.test(cuil)) {
    // "El campo debe ser numérico";
    return false;
  }

  if (cuil.length !== 11) {
    // Longitud inválida
    return false;
  }

  let acumulado = 0;
  let digitos = cuil.split("");
  let digito = parseInt(digitos.pop());

  for (let i = 0; i < digitos.length; i++) {
    acumulado += parseInt(digitos[9 - i]) * (2 + (i % 6));
  }

  let verif = 11 - (acumulado % 11);
  if (verif === 11) {
    verif = 0;
  }

  return digito === verif;
};

const getSearchString = () => {
  // Como tenemos un hash en la URL, location.search no siempre funciona
  if (location.search) {
    // Tenemos search, usamos eso
    return location.search;
  }

  if (location.href.indexOf("?") !== -1) {
    // Tenemos variables en la URL después del hash. Intentar obtener.
    let splitHref = location.href.split("/");
    return splitHref[splitHref.length - 1];
  }

  return "";
};

const searchQuery = queryString.parse(getSearchString());

const getEnvironment = () => {
  const currentHostname = window.location.hostname;
  const prodHosts = ["www.bbva.com.ar", "bbva.com.ar", "apps.bbva.com.ar"];
  const testHosts = ["app-work.bbva.com.ar"];

  if (prodHosts.includes(currentHostname)) {
    return {
      name: "prod",
      description: "Produccion",
      adobeLaunch:
        "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-144c155dcd64.min.js"
    };
  } else if (testHosts.includes(currentHostname)) {
    return {
      name: "qa",
      description: "Test",
      adobeLaunch:
        "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-8bd807155300-development.min.js"
    };
  }

  // No identificado? usar development
  return {
    name: "de",
    description: "Desarrollo",
    adobeLaunch:
      "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-8bd807155300-development.min.js"
  };
};

const getBranchOfficeById = (id, sucursales) => {
  let sucursal = sucursales.find(sucursal => sucursal.id === id);

  if (!sucursal) return {};

  return {
    id: sucursal.id,
    nombre: sucursal.description,
    direccion: sucursal.address.addressName,
    localidad: sucursal.address.city,
    provincia: sucursal.address.state.name
  };
};

const obtenerDeviceId = function () {
  const uIdLocal = localStorage.getItem("uId");
  if (uIdLocal) {
    return uIdLocal;
  } else {
    const uId = uuidv4();
    localStorage.setItem("uId", uId);
    return uId;
  }
};

const obtenerDeviceIdTimestamp = function () {
  const timeStampLocal = localStorage.getItem("timeStamp");
  let date = new Date();
  let time = date.toTimeString();
  let timestamp;
  let gmt;
  date = date.toISOString().match(/\d{4}-\d{2}-\d{2}[A-Z]/g);
  gmt = time.match(/[-+]\d{2}\d{2}/g);
  time = time.match(/\d{2}:\d{2}:\d{2}/g);
  timestamp = date + time + gmt;
  if (timeStampLocal) {
    return timeStampLocal;
  } else {
    localStorage.setItem("timeStamp", timestamp);
    return timestamp;
  }
};

const isValidSystemDate = function (clientDate, systemDate) {
  const fechaNacimiento = new Date(clientDate);
  const hoy = new Date(systemDate);

  const edad = hoy.getFullYear() - fechaNacimiento.getFullYear();
  const haCumplido =
    hoy.getMonth() > fechaNacimiento.getMonth() ||
    (hoy.getMonth() === fechaNacimiento.getMonth() &&
      hoy.getDate() >= fechaNacimiento.getDate());

  const edadReal = haCumplido ? edad : edad - 1;
  return edadReal >= 18 && edadReal <= 75;
};

const textUppercase = function (str) {
  return str.toUpperCase();
};

function getTitle() {
  const currentPath = window.location.hash;
  if (currentPath.includes("prescriptores")) {
    return "Derivación de crédito hipotecario";
  }
  return "Simulador de crédito hipotecario";
}

export {
  cuilGen,
  isValidDate,
  getParameterByName,
  getTrackingCodeLocal,
  formatDinero,
  formatTasa,
  obtenerTrackingCode,
  replaceAll,
  toTitleCase,
  isDev,
  formatMoney,
  _isMobile,
  get,
  idDevice,
  isValidCuil,
  normalizeCharacters,
  searchQuery,
  getEnvironment,
  obtenerDeviceId,
  obtenerDeviceIdTimestamp,
  getBranchOfficeById,
  getSearchString,
  isValidSystemDate,
  textUppercase,
  getTitle
};
